import {
  Center,
  Checkbox,
  Combobox,
  InputBase,
  Pill,
  ScrollArea,
} from '@mantine/core';
import cx from 'clsx';
import { FC, memo, useEffect } from 'react';
import defaultStyles from 'src/components/Form/styles.default.module.css';
import { Icon } from 'src/components/Icon';
import { useFormContext } from 'src/form';

import checkboxStyles from './styles/checkbox.style.module.css';
import comboboxStyles from './styles/combobox.style.module.css';
import styles from './styles/styles.module.css';
import { useMultiselect } from './use-multiselect';

export type Option = {
  label: string;
  value: string;
};

export type Props = {
  name: string;
  label: string;
  placeholder: string;
  required: boolean;
  data: Option[];
  noResultMessage?: string;
  withSearch?: boolean;
};

export const MultiSelect: FC<Props> = memo(
  ({
    name,
    data,
    label,
    placeholder,
    required,
    noResultMessage = 'Nothing found...',
    withSearch = false,
  }) => {
    const {
      allChecked,
      combobox,
      dropdownRef,
      focused,
      inputRef,
      search,
      selected,
      handleValueSelect,
      handleSelectAll,
      handleValueRemove,
      setSearch,
    } = useMultiselect(data);

    const { getInputProps, setFieldValue } = useFormContext();
    const formProps = getInputProps(name);

    useEffect(() => {
      setFieldValue(name, selected);
    }, [selected, setFieldValue, name]);

    const pills = selected
      .map((item) => (
        <Pill
          key={item}
          withRemoveButton
          onRemove={() => handleValueRemove(item)}
          classNames={{
            root: styles.pill,
          }}
        >
          {data.find(({ value }) => value === item)?.label}
        </Pill>
      ))
      .slice(0, 3);

    const options = data
      .filter(({ label }) =>
        label.toLowerCase().includes(search.trim().toLowerCase())
      )
      .map(({ label, value }) => (
        <Combobox.Option
          value={value}
          key={value}
          active={selected.includes(value)}
          onClick={() => handleValueSelect(value)}
        >
          <Checkbox
            checked={selected.includes(value)}
            classNames={{
              icon: checkboxStyles.checkboxIcon,
              input: checkboxStyles.checkboxInput,
              label: checkboxStyles.checkboxLabel,
            }}
            label={label}
            labelPosition="right"
            onChange={() => handleValueSelect(value)}
            size="md"
          />
        </Combobox.Option>
      ));

    return (
      <Combobox
        classNames={{
          dropdown: comboboxStyles.dropdown,
          option: comboboxStyles.option,
          search: comboboxStyles.search,
        }}
        store={combobox}
      >
        <Combobox.Target>
          <InputBase
            classNames={{
              input: cx({ [defaultStyles.input]: true }),
              label: defaultStyles.label,
              root: cx({
                [defaultStyles.root]: true,
                [defaultStyles.rootFocused]: focused,
              }),
              section: styles.section,
            }}
            component="button"
            label={label}
            required={required}
            type="button"
            pointer
            ref={inputRef}
            rightSection={
              <Icon
                className={cx({
                  [styles.icon]: true,
                  [styles.iconAnimated]: combobox.dropdownOpened,
                })}
                name="navigationArrow"
              />
            }
            rightSectionPointerEvents="none"
            onClick={() => combobox.toggleDropdown()}
            withAsterisk={false}
            {...formProps}
          >
            <Pill.Group className={styles.pillsGroup}>
              {pills.length ? (
                pills
              ) : (
                <span className={styles.placeholder}>{placeholder}</span>
              )}
              {selected.length > 3 && (
                <Center className={styles.rest}>{selected.length - 3}</Center>
              )}
            </Pill.Group>
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown ref={dropdownRef}>
          {withSearch && (
            <Combobox.Search
              value={search}
              onChange={(event) => {
                combobox.updateSelectedOptionIndex();
                setSearch(event.currentTarget.value);
              }}
              placeholder="Search"
            />
          )}

          {options.length ? (
            <>
              <Checkbox
                checked={allChecked}
                classNames={{
                  body: checkboxStyles.checkboxBody,
                  icon: checkboxStyles.checkboxIcon,
                  input: cx({
                    [checkboxStyles.checkboxAllInput]: true,
                    [checkboxStyles.checkboxAllInputChecked]: allChecked,
                  }),
                  label: checkboxStyles.checkboxLabel,
                  root: checkboxStyles.checkboxAll,
                }}
                onChange={handleSelectAll}
                label="Select All"
                size="md"
              />
              <ScrollArea.Autosize
                scrollbarSize="0.25rem"
                type="scroll"
                mah={200}
              >
                <Combobox.Options>{options}</Combobox.Options>
              </ScrollArea.Autosize>
            </>
          ) : (
            <Combobox.Empty className={styles.empty}>
              {noResultMessage}
            </Combobox.Empty>
          )}
        </Combobox.Dropdown>
      </Combobox>
    );
  }
);
